import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Calendar,
  Clock,
  User,
  Briefcase,
  Wrench,
  Building,
  FileText,
  Play,
  CheckCircle2,
} from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const SUBTYPE_LABELS: Record<string, string> = {
  training: "Capacitacion",
  report: "Informe",
  audit: "Auditoria",
  meeting: "Reunion",
  documentation: "Documentacion",
  cleaning: "Limpieza",
  maintenance: "Mantenimiento",
  organization: "Organizacion",
  inventory: "Inventario",
  equipment_check: "Revision de Equipos",
};

interface TaskDetailsDialogProps {
  task: any | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStatusChange?: (taskId: string, newStatus: string) => void;
}

export function TaskDetailsDialog({
  task,
  open,
  onOpenChange,
  onStatusChange,
}: TaskDetailsDialogProps) {
  if (!task) return null;

  const TypeIcon = task.task_type === "administrative" ? Briefcase : Wrench;

  const getStatusBadge = (status: string) => {
    const config: Record<string, { color: string; label: string }> = {
      pending: { color: "bg-slate-500/20 text-slate-700", label: "Pendiente" },
      in_progress: { color: "bg-blue-500/20 text-blue-700", label: "En Progreso" },
      completed: { color: "bg-green-500/20 text-green-700", label: "Completada" },
      cancelled: { color: "bg-red-500/20 text-red-700", label: "Cancelada" },
    };
    const { color, label } = config[status] || config.pending;
    return <Badge className={color}>{label}</Badge>;
  };

  const getPriorityBadge = (priority: string) => {
    const config: Record<string, { color: string; label: string }> = {
      low: { color: "bg-slate-500/20 text-slate-600", label: "Baja" },
      normal: { color: "bg-blue-500/20 text-blue-600", label: "Normal" },
      high: { color: "bg-orange-500/20 text-orange-600", label: "Alta" },
      urgent: { color: "bg-red-500/20 text-red-600", label: "Urgente" },
    };
    const { color, label } = config[priority] || config.normal;
    return <Badge className={color}>{label}</Badge>;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <TypeIcon className="w-5 h-5 text-muted-foreground" />
            <span className="text-sm font-mono text-muted-foreground">
              {task.task_number}
            </span>
          </div>
          <DialogTitle className="text-xl">{task.title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {getStatusBadge(task.status)}
            {getPriorityBadge(task.priority)}
            <Badge variant="outline">
              {task.task_type === "administrative" ? "Administrativa" : "Operativa"}
            </Badge>
            <Badge variant="outline">
              {SUBTYPE_LABELS[task.task_subtype] || task.task_subtype}
            </Badge>
          </div>

          <Separator />

          {task.description && (
            <div>
              <h4 className="text-sm font-medium mb-1">Descripcion</h4>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {task.description}
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            {task.assigned_profile && (
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Asignado a</p>
                  <p className="font-medium">{task.assigned_profile.full_name}</p>
                </div>
              </div>
            )}

            {task.customer && (
              <div className="flex items-center gap-2">
                <Building className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Cliente</p>
                  <p className="font-medium">{task.customer.company_name}</p>
                </div>
              </div>
            )}

            {task.due_date && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Fecha limite</p>
                  <p className="font-medium">
                    {format(new Date(task.due_date), "dd MMM yyyy", { locale: es })}
                  </p>
                </div>
              </div>
            )}

            {task.estimated_duration_minutes && (
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Duracion estimada</p>
                  <p className="font-medium">{task.estimated_duration_minutes} min</p>
                </div>
              </div>
            )}

            {task.started_at && (
              <div className="flex items-center gap-2">
                <Play className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Iniciada</p>
                  <p className="font-medium">
                    {format(new Date(task.started_at), "dd MMM HH:mm", { locale: es })}
                  </p>
                </div>
              </div>
            )}

            {task.completed_at && (
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-muted-foreground" />
                <div>
                  <p className="text-xs text-muted-foreground">Completada</p>
                  <p className="font-medium">
                    {format(new Date(task.completed_at), "dd MMM HH:mm", { locale: es })}
                  </p>
                </div>
              </div>
            )}

            {task.actual_duration_minutes && (
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-green-500" />
                <div>
                  <p className="text-xs text-muted-foreground">Duracion real</p>
                  <p className="font-medium">{task.actual_duration_minutes} min</p>
                </div>
              </div>
            )}
          </div>

          {task.notes && (
            <>
              <Separator />
              <div>
                <h4 className="text-sm font-medium mb-1 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Notas
                </h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {task.notes}
                </p>
              </div>
            </>
          )}

          {onStatusChange && task.status !== "completed" && task.status !== "cancelled" && (
            <>
              <Separator />
              <div className="flex gap-2">
                {task.status === "pending" && (
                  <Button
                    className="flex-1"
                    onClick={() => {
                      onStatusChange(task.id, "in_progress");
                      onOpenChange(false);
                    }}
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Iniciar Tarea
                  </Button>
                )}
                {task.status === "in_progress" && (
                  <Button
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    onClick={() => {
                      onStatusChange(task.id, "completed");
                      onOpenChange(false);
                    }}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Completar Tarea
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
